import React, { useEffect, useRef, useState } from 'react';
import ProLink from '../../shared/ProLink/ProLink';
import { miniGazelle, dataLayer } from '../../../helpers';

const getFocusableElements = el => {
    if (!el) return [];

    const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
    ];

    const focusableElements = el.querySelectorAll(focusableSelectors.join(', '));

    return Array.from(focusableElements);
};

export default function NavigationMobile({ nav, setVisible }) {
    const [menu, setMenu] = useState(nav);
    const [previousMenu, setPreviousMenu] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');

    const menuListRef = useRef(null);

    useEffect(() => {
        nav.menuItems?.map(item => {
            item.level = 1;

            item.menuItems.forEach(subItem => {
                subItem.level = 2;

                subItem.menuItems.forEach(lowItem => {
                    lowItem.level = 3;
                });
            });
        });
    }, []);

    // Focus management when menu changes
    useEffect(() => {
        setTimeout(() => {
            if (menuListRef.current) {
                const focusableElements = getFocusableElements(menuListRef.current);
                focusableElements[0].focus();
            }
        }, 150);
    }, [menu]);

    const changeMenu = async item => {
        await animate();

        setPreviousMenu(menu);
        setMenu({
            ...item,
        });
    };

    const goBack = async (reset = false) => {
        await animate();
        if (reset) {
            return setMenu(nav);
        }
        setMenu(previousMenu);
    };

    const animate = async () => {
        return new Promise(resolve => {
            const ref = menuListRef.current;

            ref.classList.remove('animate-in');
            ref.classList.add('animate-out');

            setTimeout(() => {
                ref.classList.remove('animate-out');
                ref.classList.add('animate-in');

                resolve();
            }, 100);
        });
    };

    const handleItemClick = (e, menuItemName, subItemName, menuItemLink) => {
        const formattedMenuItemName = menuItemName.toLowerCase().replace(/\s+/g, '-');
        const formattedSubItemName = subItemName.toLowerCase().replace(/\s+/g, '-');

        const typeMap = {
            'professional-products': miniGazelle.label('products'),
            solutions: miniGazelle.label('solutions'),
            'where-to-buy': miniGazelle.label('wheretobuy'),
            support: miniGazelle.label('support'),
            'contact-us': miniGazelle.label('contactus'),
            promotions: miniGazelle.label('promotions'),
            'view-all': miniGazelle.label('viewAll'),
        };

        const formattedType = typeMap[formattedMenuItemName] || formattedMenuItemName;

        dataLayer.triggerEvent('header_click', {
            detail: {
                type: formattedType,
                name: formattedSubItemName,
                link: menuItemLink,
            },
        });
    };

    // Keyboard trap inside mobile menu
    const handleKeyDown = e => {
        const validKeys = ['Tab', 'Escape'];
        if (!validKeys.includes(e.key)) return;

        const focusableElements = getFocusableElements(menuListRef.current);

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Handle tab and shift+tab to create a focus trap
        if (e.key === 'Tab') {
            if (e.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        }

        // Handle Escape key to close menu
        if (e.key === 'Escape') {
            e.preventDefault();
            if(!menu) return;

            if (menu.topLevel) {
                const hamburger = document.getElementById('hamburger');
                hamburger.focus();
                setVisible(false);
            } else {
                if (menu.level < 2) {
                    goBack(true);
                } else {
                    goBack();
                }
            }
        }
    };

    return (
        <>
            <nav
                id="mobile-navigation"
                className="tw-absolute tw-top-16 tw-bg-dark-navy tw-text-white tw-z-50 tw-w-full tw-left-0 tw-font-sst-light"
                style={{ zIndex: 100 }}
                aria-label="Mobile Navigation"
                role="dialog"
                aria-modal="true"
            >
                <div className="tw-container tw-px-12 tw-mx-auto tw-text-white tw-relative">
                    <ul onKeyDownCapture={handleKeyDown} ref={menuListRef} className="font-sst-regular" role="menu">
                        {!menu?.topLevel && (
                            <li role="none">
                                <div className="tw-my-5">
                                    <button
                                        className="mb-2 tw-relative tw-text-left"
                                        onClick={e => {
                                            if (menu.level < 2) {
                                                goBack(true);
                                            } else {
                                                goBack();
                                            }
                                        }}
                                        aria-label="Go back"
                                        role="menuitem"
                                    >
                                        <span
                                            className="tw-transform tw--rotate-180 tw-absolute"
                                            style={{
                                                left: '-30px',
                                                bottom: '3px',
                                            }}
                                            aria-hidden="true"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                                width={20}
                                                height={20}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                                />
                                            </svg>
                                        </span>
                                        <span>
                                            {{
                                                solutionsByServices: miniGazelle.label('solutionsByServices'),
                                                solutionsByIndustry: miniGazelle.label('solutionsByIndustry'),
                                            }[menu.name] || menu.name}
                                        </span>
                                    </button>
                                    <hr className="tw-border-light-blue tw-border-2" aria-hidden="true" />
                                </div>
                            </li>
                        )}
                        {menu.menuItems?.map((navItem, i) => {
                            return (
                                <li key={`nav_item_${i}`} role="none">
                                    {navItem.menuItems.length ? (
                                        <button
                                            className="tw-flex tw-justify-between tw-my-7 tw-w-full tw-text-left"
                                            onClick={e => {
                                                changeMenu(navItem);
                                                menu.topLevel && setSelectedCategory(navItem.name);
                                                handleItemClick(
                                                    e,
                                                    menu.topLevel ? navItem.name : selectedCategory,
                                                    !menu.topLevel ? navItem.name : '',
                                                    navItem.link
                                                );
                                            }}
                                            role="menuitem"
                                        >
                                            <span>
                                                {{
                                                    solutionsByServices: miniGazelle.label('solutionsByServices'),
                                                    solutionsByIndustry: miniGazelle.label('solutionsByIndustry'),
                                                }[navItem.name] || navItem.name}
                                            </span>

                                            <span
                                                className="tw-relative"
                                                style={{
                                                    right: '-30px',
                                                }}
                                                aria-hidden="true"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="currentColor"
                                                    width={20}
                                                    height={20}
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                                    />
                                                </svg>
                                            </span>
                                        </button>
                                    ) : (
                                        <div className="tw-flex tw-justify-between tw-my-5 tw-w-full">
                                            <ProLink
                                                onClick={e =>
                                                    handleItemClick(e, selectedCategory, navItem.name, navItem.link)
                                                }
                                                to={navItem.link}
                                                style={{ color: '#fff' }}
                                                role="menuitem"
                                            >
                                                {navItem.name}
                                            </ProLink>
                                        </div>
                                    )}
                                </li>
                            );
                        })}

                        {!menu?.topLevel && menu?.navigationShowViewAll && (
                            <li>
                                <hr className="tw-border-light-blue tw-border-2 tw-mb-5" aria-hidden="true" />

                                <div className="mb-3 font-sst-regular">
                                    <ProLink
                                        to={menu?.link}
                                        style={{ color: '#81dcf7' }}
                                        onClick={e => handleItemClick(e, selectedCategory, 'view-all', menu?.link)}
                                    >
                                        {miniGazelle.label('viewAll')}
                                    </ProLink>
                                </div>
                            </li>
                        )}
                    </ul>
                </div>
            </nav>
        </>
    );
}
